import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { isPathHiddenInCurrentEnv } from '../../../config/defaultMenu';
import {
  PiListMagnifyingGlassLight,
  PiFileMagnifyingGlassLight,
  PiTableLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiBookOpenTextLight,
  PiArrowRightLight,
} from 'react-icons/pi';

const TOOLS = [
  {
    name: 'Research',
    desc: 'AI-powered legal research',
    icon: PiListMagnifyingGlassLight,
    href: '/research',
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    borderColor: 'border-indigo-400',
    section: 'research',
  },
  {
    name: 'Reviews',
    desc: 'AI document review',
    icon: PiFileMagnifyingGlassLight,
    href: '/reviews',
    color: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    borderColor: 'border-sky-400',
    section: 'review',
  },
  {
    name: 'Tables',
    desc: 'AI due diligence tables',
    icon: PiTableLight,
    href: '/tables',
    color: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    borderColor: 'border-teal-400',
    section: 'tables',
  },
  {
    name: 'Data Sources',
    desc: 'Knowledge base & RAG',
    icon: PiDatabaseLight,
    href: '/data-sources',
    color: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    borderColor: 'border-cyan-400',
    section: 'data_sources',
  },
  {
    name: 'Templates',
    desc: 'Document templates',
    icon: PiFileTextLight,
    href: '/templates',
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-50 dark:bg-slate-900/20',
    borderColor: 'border-slate-400',
    section: 'templates',
  },
  {
    name: 'Playbooks',
    desc: 'Compliance playbooks',
    icon: PiBookOpenTextLight,
    href: '/playbooks',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-400',
    section: 'review',
  },
];

const ToolsGrid: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const visibleTools = TOOLS.filter(
    (tool) => hasSectionAccess(tool.section) && !isPathHiddenInCurrentEnv(tool.href),
  );
  if (visibleTools.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {visibleTools.map((tool) => {
        const Icon = tool.icon;
        return (
          <div
            key={tool.href}
            onClick={() => history.push(tool.href)}
            className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-t-2 ${tool.borderColor}
              rounded-lg p-4 cursor-pointer hover:shadow-md hover:scale-[1.02]
              transition-all duration-200 group`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl ${tool.bg} flex items-center justify-center flex-shrink-0 group-hover:shadow-sm transition-shadow`}>
                <Icon className={`w-6 h-6 ${tool.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-white">{t(tool.name)}</p>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">{t(tool.desc)}</p>
              </div>
              <PiArrowRightLight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors flex-shrink-0" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ToolsGrid;
